import React, { useEffect, useState } from 'react';
import {
  Container,
  Spinner,
  Table,
  Button,
  Badge,
  Pagination,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';

const UserTradeHistory = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalTrades: 0,
    hasNext: false,
    hasPrev: false,
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchTrades(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTrades = async (page = 1) => {
    try {
      setLoading(true);
      const response = await api.get(
        `/api/trading/history?page=${page}&limit=10`
      );

      if (response.data) {
        // Your API response structure is:
        // { success: true, message: "...", data: { trades: [...], pagination: {...} } }
        let tradesData = [];
        let paginationData = {
          currentPage: 1,
          totalPages: 1,
          totalTrades: 0,
          hasNext: false,
          hasPrev: false,
        };

        if (response.data.data && response.data.data.trades) {
          // Standard structure
          tradesData = response.data.data.trades;
          paginationData = response.data.data.pagination || paginationData;
        } else if (Array.isArray(response.data)) {
          // If response.data is directly an array
          tradesData = response.data;
          paginationData.totalTrades = tradesData.length;
        } else if (response.data.trades) {
          // If trades is directly in response.data
          tradesData = response.data.trades;
          paginationData = response.data.pagination || paginationData;
        }

        setTrades(tradesData);
        setPagination(paginationData);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to view trade history', {
          position: 'bottom-left',
          autoClose: 3000,
        });
      } else {
        toast.error(
          error.response?.data?.message || 'Failed to load trade history',
          {
            position: 'bottom-left',
            autoClose: 3000,
          }
        );
      }
      setTrades([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    fetchTrades(page);
  };

  const getResultBadge = (result) => {
    if (result === 'WIN') {
      return <Badge bg='success'>WIN</Badge>;
    } else if (result === 'LOSE') {
      return <Badge bg='danger'>LOSE</Badge>;
    }
    return <Badge bg='secondary'>{result}</Badge>;
  };

  const getTradeTypeBadge = (type) => {
    if (type === 'LONG') {
      return (
        <span style={{ color: '#28a745', fontWeight: '600' }}>LONG ↑</span>
      );
    } else if (type === 'SHORT') {
      return (
        <span style={{ color: '#dc3545', fontWeight: '600' }}>SHORT ↓</span>
      );
    }
    return <span>{type}</span>;
  };

  return (
    <Container
      fluid
      style={{
        maxWidth: isMobile ? '100%' : '85%',
        padding: 'clamp(1rem, 3vw, 1.5rem)',
        backgroundColor: '#FFFFFF',
        minHeight: '100vh',
      }}
    >
      {/* Header */}
      <div className='d-flex align-items-center mb-4'>
        <Button
          variant='link'
          onClick={() => navigate(-1)}
          className='text-dark p-0 me-2'
        >
          <i className='bi bi-arrow-left fs-5'></i>
        </Button>
        <h5 className='mb-0'>Trade History</h5>
      </div>

      {/* Total Trades */}
      <div
        className='bg-white p-3 rounded shadow-sm mb-3'
        style={{ border: '1px solid #e0e0e0' }}
      >
        <div className='d-flex justify-content-between align-items-center'>
          <span
            style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1rem)', color: '#666' }}
          >
            Total Trades
          </span>
          <span
            style={{
              fontSize: 'clamp(1.1rem, 3vw, 1.3rem)',
              fontWeight: '700',
              color: '#2F80ED',
            }}
          >
            {pagination.totalTrades || 0}
          </span>
        </div>
      </div>

      {/* Trade History Table */}
      {loading ? (
        <div className='text-center py-5'>
          <Spinner animation='border' variant='primary' />
          <p className='text-muted mt-2'>Loading trades...</p>
        </div>
      ) : trades.length === 0 ? (
        <div
          className='bg-white p-5 rounded shadow-sm text-center'
          style={{ border: '1px solid #e0e0e0' }}
        >
          <i className='bi bi-inbox fs-1 text-muted'></i>
          <p className='text-muted mt-3 mb-0'>No trades found</p>
        </div>
      ) : (
        <>
          {/* Desktop Table data */}
          {!isMobile ? (
            <div
              className='bg-white rounded shadow-sm mb-3'
              style={{ border: '1px solid #e0e0e0', overflowX: 'auto' }}
            >
              <Table
                responsive
                hover
                className='mb-0'
                style={{ minWidth: '700px' }}
              >
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th
                      style={{
                        padding: '16px 12px',
                        fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
                        fontWeight: '700',
                        color: '#333',
                      }}
                    >
                      Symbol
                    </th>
                    <th
                      style={{
                        padding: '16px 12px',
                        fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
                        fontWeight: '700',
                        color: '#333',
                      }}
                    >
                      Type
                    </th>
                    <th
                      style={{
                        padding: '16px 12px',
                        fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
                        fontWeight: '700',
                        color: '#333',
                      }}
                    >
                      Amount
                    </th>
                    <th
                      style={{
                        padding: '16px 12px',
                        fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
                        fontWeight: '700',
                        color: '#333',
                      }}
                    >
                      Entry
                    </th>
                    <th
                      style={{
                        padding: '16px 12px',
                        fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
                        fontWeight: '700',
                        color: '#333',
                      }}
                    >
                      Exit
                    </th>
                    <th
                      style={{
                        padding: '16px 12px',
                        fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
                        fontWeight: '700',
                        color: '#333',
                      }}
                    >
                      Status
                    </th>
                    <th
                      style={{
                        padding: '16px 12px',
                        fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
                        fontWeight: '700',
                        color: '#333',
                      }}
                    >
                      Result
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {trades.map((trade) => (
                    <tr key={trade.id}>
                      <td
                        style={{
                          padding: '14px 12px',
                          fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                        }}
                      >
                        <strong>{trade.symbol}</strong>
                      </td>
                      <td
                        style={{
                          padding: '14px 12px',
                          fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                        }}
                      >
                        {getTradeTypeBadge(trade.tradeType)}
                      </td>
                      <td
                        style={{
                          padding: '14px 12px',
                          fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                        }}
                      >
                        {parseFloat(trade.amount).toFixed(2)}
                      </td>
                      <td
                        style={{
                          padding: '14px 12px',
                          fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                        }}
                      >
                        ${parseFloat(trade.entryPrice).toFixed(2)}
                      </td>
                      <td
                        style={{
                          padding: '14px 12px',
                          fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                        }}
                      >
                        ${parseFloat(trade.exitPrice).toFixed(2)}
                      </td>
                      <td
                        style={{
                          padding: '14px 12px',
                          fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                        }}
                      >
                        <Badge
                          bg={trade.status === 'COMPLETED' ? 'info' : 'warning'}
                        >
                          {trade.status}
                        </Badge>
                      </td>
                      <td
                        style={{
                          padding: '14px 12px',
                          fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                        }}
                      >
                        {getResultBadge(trade.result)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            /* Mobile Cards */
            <div className='mb-3'>
              {trades.map((trade) => (
                <div
                  key={trade.id}
                  className='bg-white p-3 rounded shadow-sm mb-3'
                  style={{ border: '1px solid #e0e0e0' }}
                >
                  <div className='d-flex justify-content-between align-items-center mb-2'>
                    <div>
                      <strong style={{ fontSize: '1.1rem' }}>
                        {trade.symbol}
                      </strong>
                    </div>
                    <div>{getTradeTypeBadge(trade.tradeType)}</div>
                  </div>
                  <div className='d-flex justify-content-between mb-1'>
                    <span style={{ color: '#666', fontSize: '0.9rem' }}>
                      Amount:
                    </span>
                    <span style={{ fontWeight: '600', fontSize: '0.9rem' }}>
                      {parseFloat(trade.amount).toFixed(2)}
                    </span>
                  </div>
                  <div className='d-flex justify-content-between mb-1'>
                    <span style={{ color: '#666', fontSize: '0.9rem' }}>
                      Entry:
                    </span>
                    <span style={{ fontWeight: '600', fontSize: '0.9rem' }}>
                      ${parseFloat(trade.entryPrice).toFixed(2)}
                    </span>
                  </div>
                  <div className='d-flex justify-content-between mb-2'>
                    <span style={{ color: '#666', fontSize: '0.9rem' }}>
                      Exit:
                    </span>
                    <span style={{ fontWeight: '600', fontSize: '0.9rem' }}>
                      ${parseFloat(trade.exitPrice).toFixed(2)}
                    </span>
                  </div>
                  <div className='d-flex justify-content-between align-items-center'>
                    <Badge
                      bg={trade.status === 'COMPLETED' ? 'info' : 'warning'}
                    >
                      {trade.status}
                    </Badge>
                    {getResultBadge(trade.result)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div
              className='bg-white p-3 rounded shadow-sm'
              style={{ border: '1px solid #e0e0e0' }}
            >
              <div className='d-flex flex-column flex-md-row justify-content-between align-items-center gap-3'>
                <div
                  className='text-muted order-1 order-md-0'
                  style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)' }}
                >
                  Showing{' '}
                  {Math.min(
                    (pagination.currentPage - 1) * 10 + 1,
                    pagination.totalTrades
                  )}{' '}
                  to{' '}
                  {Math.min(
                    pagination.currentPage * 10,
                    pagination.totalTrades
                  )}{' '}
                  of {pagination.totalTrades} users
                </div>
                <Pagination className='mb-0' size={isMobile ? 'sm' : undefined}>
                  <Pagination.Prev
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrev}
                    style={{
                      fontSize: 'clamp(0.8rem, 2vw, 0.9rem)',
                    }}
                  />

                  {(() => {
                    const pages = [];
                    const { currentPage, totalPages } = pagination;

                    if (totalPages <= 4) {
                      // Show all pages if total is 4 or less
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(
                          <Pagination.Item
                            key={i}
                            active={currentPage === i}
                            onClick={() => handlePageChange(i)}
                            style={{ fontSize: 'clamp(0.8rem, 2vw, 0.9rem)' }}
                          >
                            {i}
                          </Pagination.Item>
                        );
                      }
                    } else {
                      // Determine which 2 pages to show at the start
                      let startPages = [currentPage, currentPage + 1];

                      // Adjust if we're near the end
                      if (currentPage >= totalPages - 1) {
                        startPages = [totalPages - 1, totalPages];
                      }

                      // Show the 2 consecutive pages based on current position
                      startPages.forEach((pageNum) => {
                        pages.push(
                          <Pagination.Item
                            key={pageNum}
                            active={currentPage === pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            style={{ fontSize: 'clamp(0.8rem, 2vw, 0.9rem)' }}
                          >
                            {pageNum}
                          </Pagination.Item>
                        );
                      });

                      // Show ellipsis only if there's a gap before last 2 pages
                      if (startPages[1] < totalPages - 2) {
                        pages.push(
                          <Pagination.Ellipsis key='ellipsis' disabled />
                        );
                      }

                      // Always show last 2 pages if not already shown
                      if (startPages[1] < totalPages - 1) {
                        for (let i = totalPages - 1; i <= totalPages; i++) {
                          pages.push(
                            <Pagination.Item
                              key={i}
                              active={currentPage === i}
                              onClick={() => handlePageChange(i)}
                              style={{ fontSize: 'clamp(0.8rem, 2vw, 0.9rem)' }}
                            >
                              {i}
                            </Pagination.Item>
                          );
                        }
                      }
                    }

                    return pages;
                  })()}

                  <Pagination.Next
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNext}
                    style={{
                      fontSize: 'clamp(0.8rem, 2vw, 0.9rem)',
                    }}
                  />
                </Pagination>
              </div>
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default UserTradeHistory;
